import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { DOCTOR, CONCERNS } from "@/data";
import { Reveal } from "./motion";
import { Phone, MessageCircle, MapPin, Mail, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", concern: "", preferred_date: "", message: "" };

export const AppointmentForm = ({ compact = false, onDone }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waMessage = () => {
    const lines = [
      "Hello Dr. Pranidha Shree, I would like to book a consultation.",
      form.name && `Name: ${form.name}`,
      form.phone && `Phone: ${form.phone}`,
      form.concern && `Concern: ${form.concern}`,
      form.preferred_date && `Preferred date: ${form.preferred_date}`,
      form.message && `Message: ${form.message}`,
    ].filter(Boolean);
    return encodeURIComponent(lines.join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Request received", {
        description: "Dr. Pranidha's team will reach out to you shortly.",
      });
      window.open(`https://wa.me/${DOCTOR.phoneIntl}?text=${waMessage()}`, "_blank");
      setForm(initial);
      onDone && onDone();
    } catch (err) {
      toast.error("Something went wrong", { description: "Please call or WhatsApp us directly." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#F6F4EE] rounded-xl px-4 py-3 text-[#1A1C1A] placeholder:text-[#9d9b96] border border-transparent focus:border-[#B9735A] focus:bg-white outline-none transition-colors duration-300";

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="appointment-form">
      <div className={`grid gap-3.5 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input className={inputCls} placeholder="Full name*" value={form.name} onChange={set("name")} data-testid="form-name" />
        <input className={inputCls} placeholder="Phone number*" value={form.phone} onChange={set("phone")} data-testid="form-phone" />
      </div>
      <div className={`grid gap-3.5 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input className={inputCls} type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid="form-email" />
        <select className={inputCls} value={form.concern} onChange={set("concern")} data-testid="form-concern">
          <option value="">Area of concern (optional)</option>
          {CONCERNS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <input className={inputCls} placeholder="Preferred date (optional)" value={form.preferred_date} onChange={set("preferred_date")} data-testid="form-date" />
      <textarea className={inputCls} rows={compact ? 2 : 3} placeholder="Tell us briefly how we can help (optional)" value={form.message} onChange={set("message")} data-testid="form-message" />
      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B9735A] text-[#FDFBF7] font-medium px-6 py-3.5 hover:shadow-lg hover:shadow-[#B9735A]/30 disabled:opacity-70 transition-[box-shadow] duration-300"
        data-testid="form-submit"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
        {loading ? "Sending..." : "Request Appointment"}
      </button>
      <p className="text-xs text-[#9d9b96] text-center">
        Fields marked * are required. We also send your request to WhatsApp for a faster response.
      </p>
    </form>
  );
};

export const Appointment = () => (
  <section id="appointment" className="py-24 sm:py-32 bg-[#F6F4EE] scroll-mt-24" data-testid="appointment">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="overline text-[#B9735A] mb-5">Book a Consultation</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl text-[#1A1C1A] tracking-tight">
            Let's talk about <span className="italic text-[#B9735A]">your care</span>
          </h2>
          <p className="mt-6 text-[#4A4D4A] leading-relaxed">
            Whether you need a diagnosis, a second opinion, or ongoing survivorship care —
            reach out. Every enquiry is handled with confidentiality and compassion.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 space-y-3">
            <a href={`tel:+${DOCTOR.phoneIntl}`} className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-5 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300" data-testid="contact-call">
              <div className="w-11 h-11 rounded-xl bg-[#B9735A]/10 text-[#B9735A] flex items-center justify-center"><Phone size={18} /></div>
              <div><p className="text-xs text-[#6B7A6A]">Call</p><p className="text-[#1A1C1A] font-medium">{DOCTOR.phone}</p></div>
            </a>
            <a href={`https://wa.me/${DOCTOR.phoneIntl}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-5 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300" data-testid="contact-whatsapp">
              <div className="w-11 h-11 rounded-xl bg-[#25D366]/15 text-[#128C4A] flex items-center justify-center"><MessageCircle size={18} /></div>
              <div><p className="text-xs text-[#6B7A6A]">WhatsApp</p><p className="text-[#1A1C1A] font-medium">{DOCTOR.phone}</p></div>
            </a>
            <a href={`mailto:${DOCTOR.email}`} className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-5 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300" data-testid="contact-email">
              <div className="w-11 h-11 rounded-xl bg-[#6B7A6A]/12 text-[#6B7A6A] flex items-center justify-center"><Mail size={18} /></div>
              <div><p className="text-xs text-[#6B7A6A]">Email</p><p className="text-[#1A1C1A] font-medium break-all">{DOCTOR.email}</p></div>
            </a>
            <div className="flex items-center gap-4 rounded-2xl bg-white border border-black/5 p-5">
              <div className="w-11 h-11 rounded-xl bg-[#1A1C1A]/8 text-[#1A1C1A] flex items-center justify-center"><MapPin size={18} /></div>
              <div><p className="text-xs text-[#6B7A6A]">Visit</p><p className="text-[#1A1C1A] font-medium">{DOCTOR.hospital}</p></div>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <Reveal delay={0.15}>
          <div className="rounded-3xl bg-white border border-black/5 p-7 sm:p-10 shadow-xl shadow-black/5">
            <AppointmentForm />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
